import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, Link2, RefreshCw, Trash2 } from 'lucide-react';

interface CredentialsInfo {
  linked: boolean;
  school_code?: string;
  login_email?: string;
  last_sync_at?: string | null;
  last_sync_error?: string | null;
  last_sync_duration_ms?: number | null;
  auto_sync_enabled?: boolean;
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

const PortalPage: React.FC = () => {
  const [info, setInfo] = useState<CredentialsInfo | null>(null);
  const [schoolCode, setSchoolCode] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = async () => {
    try {
      const r = await api<CredentialsInfo>('/api/portal/credentials');
      setInfo(r);
      if (r.linked) {
        setSchoolCode(r.school_code || '');
        setLoginEmail(r.login_email || '');
      }
    } catch (err: any) {
      setMessage({ kind: 'err', text: err?.message || 'Fehler beim Laden' });
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schoolCode || !loginEmail || !password) {
      setMessage({ kind: 'err', text: 'Bitte alle Felder ausfüllen. Passwort muss zum Speichern eingegeben werden.' });
      return;
    }
    setBusy(true); setMessage(null);
    try {
      await api('/api/portal/credentials', {
        method: 'PUT',
        body: JSON.stringify({ school_code: schoolCode, login_email: loginEmail, password }),
      });
      setPassword('');
      setMessage({ kind: 'ok', text: 'Zugangsdaten gespeichert.' });
      await load();
    } catch (err: any) {
      setMessage({ kind: 'err', text: err?.message || 'Speichern fehlgeschlagen' });
    } finally {
      setBusy(false);
    }
  };

  const triggerSync = async () => {
    setBusy(true); setMessage(null);
    try {
      const r = await api<{ ok: boolean; counts: any; duration_ms: number }>('/api/portal/sync', { method: 'POST' });
      setMessage({ kind: 'ok', text: `Sync ok: ${r.counts.timetableEntries} Std · ${r.counts.homeworkNew} HA neu · ${r.counts.eventsNew} Termine neu · ${r.counts.substitutionsNew} Vertretungen.` });
      await load();
    } catch (err: any) {
      setMessage({ kind: 'err', text: err?.message || 'Sync fehlgeschlagen' });
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm('Schul-Portal-Verknüpfung wirklich entfernen?')) return;
    setBusy(true); setMessage(null);
    try {
      await api('/api/portal/credentials', { method: 'DELETE' });
      setSchoolCode(''); setLoginEmail(''); setPassword('');
      setMessage({ kind: 'ok', text: 'Verknüpfung entfernt.' });
      await load();
    } catch (err: any) {
      setMessage({ kind: 'err', text: err?.message || 'Löschen fehlgeschlagen' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <Link2 className="w-6 h-6 text-blue-400" />
        <h1 className="text-xl font-semibold">Schul-Portal verknüpfen</h1>
      </header>

      <main className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="font-semibold mb-3">Status</h2>
          {info?.linked ? (
            <ul className="text-sm text-gray-300 space-y-1">
              <li>Schulkürzel: <code className="text-white">{info.school_code}</code></li>
              <li>Login: <code className="text-white">{info.login_email}</code></li>
              <li>Letzte Synchronisation: {formatDateTime(info.last_sync_at)}{info.last_sync_duration_ms ? ` (${Math.round(info.last_sync_duration_ms / 100) / 10}s)` : ''}</li>
              {info.last_sync_error && <li className="text-red-400">Letzter Fehler: {info.last_sync_error}</li>}
            </ul>
          ) : (
            <p className="text-gray-400 text-sm">Noch nicht verknüpft.</p>
          )}

          {info?.linked && (
            <div className="flex gap-3 mt-4 flex-wrap items-center">
              <button onClick={triggerSync} disabled={busy}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-2">
                <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Jetzt synchronisieren
              </button>
              <label className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-2 rounded-lg cursor-pointer">
                <input type="checkbox" checked={info.auto_sync_enabled ?? false}
                  onChange={async e => {
                    setBusy(true);
                    try {
                      await api('/api/portal/auto-sync', { method: 'POST', body: JSON.stringify({ enabled: e.target.checked }) });
                      await load();
                    } catch (err: any) { setMessage({ kind: 'err', text: err?.message || 'Fehler' }); }
                    finally { setBusy(false); }
                  }}
                  className="accent-blue-500" />
                Auto-Sync (2×/Tag)
              </label>
              <button onClick={unlink} disabled={busy}
                className="ml-auto bg-red-600/80 hover:bg-red-700 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Verknüpfung entfernen
              </button>
            </div>
          )}
        </section>

        <section className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="font-semibold mb-4">{info?.linked ? 'Zugangsdaten aktualisieren' : 'Schul-Portal verknüpfen'}</h2>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Schulkürzel</label>
              <input type="text" value={schoolCode} onChange={e => setSchoolCode(e.target.value)}
                placeholder="z. B. hsgyhoe"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">E-Mail (Schul-Portal-Login)</label>
              <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                placeholder="vorname.nachname@…"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Passwort (Schul-Portal)</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder={info?.linked ? '••• (neu eingeben zum Aktualisieren)' : 'Portal-Passwort'}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500" />
              <p className="text-xs text-gray-500 mt-1">Wird verschlüsselt in der Datenbank gespeichert und ausschließlich für die Synchronisation verwendet.</p>
            </div>
            <button type="submit" disabled={busy}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">
              {busy ? 'Speichert…' : 'Speichern'}
            </button>
          </form>
        </section>

        {message && (
          <div className={`rounded-lg px-4 py-3 text-sm ${message.kind === 'ok' ? 'bg-green-500/10 border border-green-500/40 text-green-300' : 'bg-red-500/10 border border-red-500/40 text-red-300'}`}>
            {message.text}
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalPage;
